import axios from 'axios';

// API base URL - adjust according to your backend configuration
const API_BASE_URL = process.env.VUE_APP_SERVER_URL || 'http://localhost:5000';

class MistralParserService {
  /**
   * Get authentication token from localStorage
   */
  _getAuthToken() {
    return localStorage.getItem('accessToken');
  }

  /**
   * Analyse un texte vocal d'observation complète et extrait toutes les informations structurées
   * @param {string} voiceText - Le texte transcrit de l'observation vocale complète
   * @param {Array} competenceCategories - Les catégories de compétences disponibles
   * @returns {Promise<Object>} - Données structurées pour pré-remplir le formulaire
   */
  async parseCompleteObservation(voiceText, competenceCategories) {
    try {
      const token = this._getAuthToken();
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add authentication token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Call backend API for analysis
      const response = await axios.post(
        `${API_BASE_URL}/observations/analyze-vocal`,
        {
          voice_text: voiceText,
          competence_categories: competenceCategories
        },
        {
          headers,
          withCredentials: true // Ensure cookies are sent for session-based auth
        }
      );

      // The backend already returns validated and normalized data
      return response.data;

    } catch (error) {
      if (error.response) {
        // If service unavailable (Mistral not configured), use fallback
        if (error.response.status === 503) {
          return this.fallbackParsing(voiceText);
        }

        // For other errors, throw with backend message
        throw new Error(error.response.data.detail || 'Error analyzing observation');
      }

      // Network error or other issues - use fallback
      return this.fallbackParsing(voiceText);
    }
  }


  /**
   * Parsing simple en cas d'échec de l'API Mistral
   */
  fallbackParsing(voiceText) {

    const lowerText = voiceText.toLowerCase();

    // Détection simple de certains éléments
    const environments = {
      'école': 'interior',
      'classe': 'interior',
      'maison': 'interior',
      'cuisine': 'interior',
      'parc': 'exterior',
      'jardin': 'exterior',
      'dehors': 'exterior'
    };

    const socialSpheres = {
      'maison': 'privee',
      'famille': 'privee',
      'école': 'publique',
      'parc': 'publique',
      'magasin': 'publique'
    };

    let locationType = '';
    let socialSphere = '';
    let locationDescription = '';

    for (const [keyword, type] of Object.entries(environments)) {
      if (lowerText.includes(keyword)) {
        locationType = type;
        locationDescription = keyword.charAt(0).toUpperCase() + keyword.slice(1);
        break;
      }
    }

    for (const [keyword, sphere] of Object.entries(socialSpheres)) {
      if (lowerText.includes(keyword)) {
        socialSphere = sphere;
        break;
      }
    }

    // Détection des personnes présentes
    const peoplePresent = [];
    if (lowerText.includes('camarade') || lowerText.includes('ami')) peoplePresent.push('Camarades');
    if (lowerText.includes('parent') || lowerText.includes('maman') || lowerText.includes('papa')) peoplePresent.push('Parents');
    if (lowerText.includes('professeur') || lowerText.includes('enseignant') || lowerText.includes('prof')) peoplePresent.push('Professeur');
    if (lowerText.includes('éducateur')) peoplePresent.push('Éducateur');

    return {
      observationSource: 'direct',
      startTime: '',
      endTime: '',
      peoplePresent,
      observerPosition: '',

      location: {
        type: locationType,
        description: locationDescription
      },

      environment: {
        socialSphere,
        noiseLevel: '',
        lighting: '',
        peopleCount: '',
        otherSensory: ''
      },

      youngBefore: {
        emotionalState: '',
        behavior: '',
        physiologicalNeeds: [],
        preparation: ''
      },

      observerBefore: {
        emotionalStates: [],
        availability: ''
      },

      factualDescription: voiceText,

      interventions: {
        types: [],
        description: ''
      },

      youngAfter: {
        emotionalState: '',
        behavior: '',
        decompensationSigns: []
      },

      accompanistActions: [],

      competences: [],
      competencesContext: '',

      additionalNotes: ''
    };
  }

  /**
   * Vérifie si l'API backend est disponible
   * Note: Cette méthode retourne toujours true car la vérification
   * de la configuration Mistral se fait maintenant côté backend
   */
  isConfigured() {
    // Backend handles Mistral configuration check
    return true;
  }
}

export default new MistralParserService();
